import * as React from 'react';
import classnames from 'classnames';
import { translate } from 'react-i18next';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';

import theme from './AddressList.scss';
import { List, AutoSizer } from 'react-virtualized';

import { LocationCard } from 'common/components/core/card/LocationCard';
import { Input, InputTypes } from 'common/components/core/input';
import _ from 'lodash';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import Icon from 'common/components/core/icon';
import { Button, ButtonTypes } from 'common/components/core/button';
import { Checkbox } from 'common/components/core/checkbox';
import LocationFilterPopoverButton from './LocationFilterPopoverButton';
import { SizeEnum } from 'common/enums/UIEnums';
import { dashboardColors } from 'common/enums/ColorEnum';
import { HomebaseType } from 'common/models/internal/MasterHomebase';

const ListWithKeyEvents = KeyEventHandler(List);
export const addressListFactory = ({ CardComponent }) => {
    class AddressList extends React.Component {
        static defaultProps = {
            renderLocationFilterButton
        };
        constructor(props) {
            super(props);

            this.debouncedSearchTextChange = _.debounce(
                props.handleSearchTextChange,
                100
            );
            this.state = {
                internalSearchText: props.searchText
            };
        }

        UNSAFE_componentWillReceiveProps(nextProps) {
            const {
                locations,
                expandedLocationId,
                shouldShowOriginalColor,
                checkedCardIds,
                isValidateGeocodesActive
            } = this.props;
            const {
                locations: nextLocations,
                expandedLocationId: nextExpandedLocationId,
                shouldShowOriginalColor: nextShouldShowOriginalColor,
                checkedCardIds: nextCheckedCardIds,
                isValidateGeocodesActive: nextIsValidateGeocodesActive
            } = nextProps;
            if (
                (locations &&
                    (expandedLocationId !== nextExpandedLocationId ||
                        locations !== nextLocations ||
                        checkedCardIds !== nextCheckedCardIds ||
                        isValidateGeocodesActive !==
                            nextIsValidateGeocodesActive)) ||
                shouldShowOriginalColor !== nextShouldShowOriginalColor
            ) {
                if (this.addressList) {
                    this.addressList.recomputeRowHeights();
                }
            }
        }

        NUM_OF_LOADING_ROWS = 20;

        handleSearchTextChange = (value, ...args) => {
            this.setState({ internalSearchText: value });
            this.debouncedSearchTextChange(value, ...args);
        };

        isCardChecked = (id, checkedCardIds) => {
            return checkedCardIds?.includes(id);
        };

        isCardExpanded = (id, expandedLocationId) => {
            return id === expandedLocationId;
        };

        isCardSelected = (id, selectedLocationId) => {
            return id === selectedLocationId;
        };

        getCardHeight = ({ index }) => {
            const {
                locations,
                selectedLocationId,
                expandedLocationId,
                handleVerify,
                showLatLng,
                getCardHeight,
                filterAddress,
                isValidateGeocodesActive
            } = this.props;
            if (getCardHeight) {
                return getCardHeight({ index });
            }
            let baseHeight = 91;
            if (handleVerify) {
                baseHeight = baseHeight + 16;
            }
            if (!locations) {
                return baseHeight;
            }
            const location = locations.get(index);
            if (
                isValidateGeocodesActive &&
                location.getIsApplySystemGeocodeVisible()
            ) {
                baseHeight = baseHeight + 24;
            }
            const { contactPoint, id: locationId, geometry } = location;
            if (
                !location.address ||
                (filterAddress && location.address
                    ? !location.address.getFilteredFullAddress()
                    : !location.address.getFullAddress())
            ) {
                baseHeight = baseHeight - 39;
            }
            if (
                (geometry && geometry.latLng && showLatLng) ||
                (isValidateGeocodesActive && location.geocodeVerifiedGeometry)
            ) {
                baseHeight = baseHeight + 18;
            }
            if (
                this.isCardSelected(locationId, selectedLocationId) &&
                this.isCardExpanded(locationId, expandedLocationId) &&
                contactPoint
            ) {
                return baseHeight + 92;
            } else if (
                this.isCardSelected(locationId, selectedLocationId) &&
                this.isCardExpanded(locationId, expandedLocationId) &&
                !contactPoint
            ) {
                return baseHeight + 68;
            } else {
                return baseHeight;
            }
        };

        getAddressCardByIndex = ({ key, index, style }) => {
            var {
                isLoading,
                expandedLocationId,
                selectedLocationId,
                locations,
                handleCardClick,
                handleResetLocation,
                handleAddressChange,
                handleVerify,
                getAddressCardByIndex,
                handleCardToggle,
                checkedCardIds,
                theme,
                isLocationEditInProgress
            } = this.props;
            if (getAddressCardByIndex) {
                return getAddressCardByIndex({ key, index, style });
            }
            if (isLoading) {
                return (
                    <CardComponent
                        key={key}
                        theme={theme}
                        style={style}
                        isLoading={isLoading}
                    />
                );
            }
            const {
                shouldShowOriginalColor,
                isEditable,
                isValidateGeocodesActive,
                getRadiusAndCenter,
                onApplySystemGeocode
            } = this.props;
            var location = locations.get(index);
            const {
                contactPoint,
                id: locationId,
                code: locationCode,
                index: locationIndex,
                isOriginal,
                geometry,
                verified,
                originalGeometry,
                address
            } = location;
            const { latLng, confidence: currentConfidence } = geometry;
            let originalConfidence;
            if (!isOriginal) {
                originalConfidence = originalGeometry
                    ? originalGeometry.getIn(['confidence'], 'LOW')
                    : 'LOW';
            }
            const fullAddress = address && address.getFullAddress();
            return (
                <CardComponent
                    autoFocusSearchBox={false}
                    getRadiusAndCenter={getRadiusAndCenter}
                    key={key}
                    style={style}
                    originalConfidence={
                        location.type === HomebaseType.Crossdock
                            ? null
                            : originalConfidence
                    }
                    currentConfidence={
                        location.type === HomebaseType.Crossdock
                            ? 'HIGH'
                            : currentConfidence
                    }
                    isLoading={isLoading}
                    id={locationId}
                    index={locationIndex}
                    theme={theme}
                    shouldShowOriginalColor={shouldShowOriginalColor}
                    isVerified={verified}
                    isSelected={this.isCardSelected(
                        locationId,
                        selectedLocationId
                    )}
                    isExpanded={this.isCardExpanded(
                        locationId,
                        expandedLocationId
                    )}
                    isCardChecked={this.isCardChecked(
                        locationId,
                        checkedCardIds
                    )}
                    onToggleCard={handleCardToggle}
                    isResetEnabled={!isOriginal}
                    onVerify={handleVerify}
                    onReset={handleResetLocation}
                    onCardClick={handleCardClick}
                    label={locationCode}
                    address={fullAddress}
                    onAddressChange={handleAddressChange}
                    isEditable={
                        isEditable && location.type !== HomebaseType.Crossdock
                    }
                    isNonGeocoded={latLng ? false : true}
                    customerName={contactPoint && contactPoint.name}
                    customerContact={contactPoint && contactPoint.number}
                    locationEntity={location}
                    isValidateGeocodesActive={isValidateGeocodesActive}
                    onApplySystemGeocode={onApplySystemGeocode}
                    isEditInProgress={isLocationEditInProgress}
                />
            );
        };

        getAddressListRef = ref => {
            const { getAddressListRef } = this.props;
            getAddressListRef && getAddressListRef(ref);
            this.addressList = ref;
        };
        handleClearText = () => {
            const { handleClearText } = this.props;
            this.setState({ internalSearchText: '' });
            handleClearText && handleClearText();
        };

        renderToggleAll = () => {
            const {
                locations,
                checkedCardIds,
                t,
                handleToggleAll,
                onApplySelectedSytemGeocodes
            } = this.props;
            const locationsCount = locations.size;
            if (!locationsCount) {
                return null;
            }
            const checkedLocationCount = checkedCardIds.length;
            const checked = checkedLocationCount === locationsCount;
            let checkboxLabel = t('geocodeMap.locationsCount', {
                count: locationsCount
            });
            if (checkedLocationCount > 0) {
                checkboxLabel = t('geocodeMap.locationsSelected', {
                    selectedCount: checkedLocationCount,
                    count: locationsCount
                });
            }

            return (
                <div className={theme.listHeader}>
                    <Checkbox
                        smallCheckbox
                        onChange={handleToggleAll}
                        label={checkboxLabel}
                        checked={checked}
                        theme={theme}
                    />
                    {this.showApplyAllSystemGeocode() ? (
                        <Button
                            semibold
                            buttonType={ButtonTypes.Text}
                            color={dashboardColors.darkSkyBlue.name}
                            uppercase
                            ripple={false}
                            size={SizeEnum.Small}
                            className={theme.applySystemGeocodeBtn}
                            theme={theme}
                            label={t('geocodeMap.applySystemGeocode')}
                            onClick={onApplySelectedSytemGeocodes}
                        />
                    ) : null}
                </div>
            );
        };

        showApplyAllSystemGeocode = () => {
            const {
                locations,
                checkedCardIds,
                isValidateGeocodesActive
            } = this.props;

            const anyLocationHasApplySystemGeocode = locations
                .filter(location => checkedCardIds.includes(location.id))
                .some(location => location.getIsApplySystemGeocodeVisible());
            return (
                checkedCardIds.length > 0 &&
                isValidateGeocodesActive &&
                anyLocationHasApplySystemGeocode
            );
        };

        render() {
            var {
                locations,
                t,
                handleGoToNextItem,
                handleGoToPreviousItem,
                handleEscape,
                handleEnter,
                positionInSortedList,
                handleClearText,
                scrollToAlignment,
                handleToggleAll,
                renderLocationFilterButton,
                listFilters,
                theme
            } = this.props;
            const { internalSearchText } = this.state;
            const searchIcon = (
                <Icon
                    theme={theme}
                    className={theme.searchIcon}
                    icon="search"
                />
            );
            return (
                <div className={theme.addressListSidebar}>
                    <div
                        className={classnames(theme.searchWrap, {
                            [theme.hasClearBtn]: handleClearText
                        })}
                    >
                        <Input
                            icon={searchIcon}
                            element="input"
                            inputType={InputTypes.Solid}
                            type="text"
                            autoFocus
                            theme={theme}
                            placeholder={t('searchMarkers')}
                            onChange={this.handleSearchTextChange}
                            value={internalSearchText}
                            clearableInput
                            onClearInput={
                                handleClearText && this.handleClearText
                            }
                            className={classnames({
                                [theme.hasFilters]: !!listFilters
                            })}
                            rightIcon={renderLocationFilterButton(this.props)}
                        />
                    </div>
                    {handleToggleAll ? this.renderToggleAll() : null}
                    <div className={theme.addressListWrap}>
                        {locations && locations.size === 0 ? (
                            <div className={theme.searchEmpty}>
                                <Icon
                                    iconClass="search"
                                    theme={theme}
                                    className={theme.searchEmptyIcon}
                                />
                                <span className={theme.text}>
                                    {t('noResultsFound')}
                                </span>
                            </div>
                        ) : (
                            <AutoSizer>
                                {({ height, width }) => (
                                    <ListWithKeyEvents
                                        theme={theme}
                                        scrollToAlignment={scrollToAlignment}
                                        onNext={handleGoToNextItem}
                                        onPrevious={handleGoToPreviousItem}
                                        onEscape={handleEscape}
                                        onEnter={handleEnter}
                                        getElementRef={this.getAddressListRef}
                                        height={height}
                                        rowHeight={this.getCardHeight}
                                        rowCount={
                                            locations
                                                ? locations.size
                                                : this.NUM_OF_LOADING_ROWS
                                        }
                                        width={width}
                                        scrollToIndex={positionInSortedList}
                                        rowRenderer={this.getAddressCardByIndex}
                                        rowClassName={this.getRowClassName}
                                        useCapture={false}
                                        isEditable={this.props.isEditable}
                                    />
                                )}
                            </AutoSizer>
                        )}
                    </div>
                </div>
            );
        }
    }

    return compose(themr(null, theme), translate())(AddressList);
};
const renderLocationFilterButton = props => {
    const { onFilterChange, listFilters, appliedFilters } = props;
    if (!listFilters) {
        return null;
    }
    return (
        <LocationFilterPopoverButton
            filters={listFilters}
            appliedFilters={appliedFilters}
            onFilterChange={onFilterChange}
        />
    );
};
const DefaultAddressList = addressListFactory({ CardComponent: LocationCard });
export { DefaultAddressList as AddressList };
export default addressListFactory;
